import type { ChatMessage } from "../types";

const DAY_MS = 86_400_000;

// Bucketing is on CALENDAR days, not elapsed hours, so a 1am message reads as "Today".
export function startOfLocalDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function dayLabel(dayStart: number, now: number, locale?: string): string {
  const daysAgo = Math.round((startOfLocalDay(now) - dayStart) / DAY_MS);
  if (daysAgo <= 0) return "Today";
  if (daysAgo === 1) return "Yesterday";
  return new Date(dayStart).toLocaleDateString(locale, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export type TranscriptItem =
  | { kind: "message"; message: ChatMessage }
  | {
      kind: "separator";
      id: string;
      label: string;
      sublabel?: string;
      emphasis?: boolean;
    };

export function buildTranscriptItems(messages: ChatMessage[], now: number): TranscriptItem[] {
  if (messages.length === 0) return [];

  const days = messages.map((m) => startOfLocalDay(m.createdAt));
  const spansMultipleDays = new Set(days).size > 1;
  const today = startOfLocalDay(now);

  const items: TranscriptItem[] = [];
  let prevDay: number | null = null;

  messages.forEach((message, i) => {
    const day = days[i];
    const dayChanged = prevDay !== null && day !== prevDay;
    const isBoundary = i > 0 && !!messages[i - 1].historical && !message.historical;

    if (isBoundary) {
      const continuedToday = dayChanged && day === today;
      items.push({
        kind: "separator",
        id: `sep-new-${message.id}`,
        label: continuedToday ? "Continued today" : "New messages",
        sublabel: dayChanged && !continuedToday ? dayLabel(day, now) : undefined,
        emphasis: true,
      });
    } else if (i === 0 && message.historical) {
      items.push({
        kind: "separator",
        id: "sep-previous",
        label: "Previous conversation",
        sublabel: spansMultipleDays ? dayLabel(day, now) : undefined,
      });
    } else if (spansMultipleDays && (i === 0 || dayChanged)) {
      items.push({
        kind: "separator",
        id: `sep-day-${day}`,
        label: dayLabel(day, now),
      });
    }

    items.push({ kind: "message", message });
    prevDay = day;
  });

  return items;
}
